/**
 * Named review sessions for grouping queue items and submitted reviews.
 */

import { useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faSpinner, faTrashCan } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import SelectDropdown, { type SelectOption } from "@/components/ui/select-dropdown";
import { Button } from "@/components/ui/button/Button";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "@/components/ui/dialog/Dialog";
import { INTEGRATIONS_SELECT_BUTTON_CLASS } from "@/components/ops/triggers/email-triggers/integrationSelectStyles";
import { agentReviewAPI, type ReviewSessionRecord } from "@/services/api/agentReview";
import { DeleteSessionDialog } from "./DeleteSessionDialog";

export function suggestedSessionName(ordinal: number): string {
  const today = new Date().toLocaleDateString("en-GB");
  return `Session ${ordinal} (${today})`;
}

interface ReviewSessionsPanelProps {
  catalogueKey: string | null;
  sessionId: string | null;
  sessions: ReviewSessionRecord[];
  sessionsLoading?: boolean;
  taskTypeLabel: string;
  canDelete?: boolean;
  onSessionIdChange: (sessionId: string | null) => void;
  onSessionsUpdated?: () => void | Promise<void>;
}

export function ReviewSessionsPanel({
  catalogueKey,
  sessionId,
  sessions,
  sessionsLoading = false,
  taskTypeLabel,
  canDelete = false,
  onSessionIdChange,
  onSessionsUpdated,
}: ReviewSessionsPanelProps) {
  const [creating, setCreating] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [error, setError] = useState<string | null>(null);

  const suggestedName = useMemo(
    () => suggestedSessionName(sessions.length + 1),
    [sessions.length],
  );

  const sessionOptions = useMemo<SelectOption<string>[]>(
    () => sessions.map((session) => ({ value: session.session_id, label: session.name })),
    [sessions],
  );

  const selectedSessionId = sessionId ?? "";

  const selectedSessionName =
    sessionOptions.find((option) => option.value === selectedSessionId)?.label ?? "this session";

  const openCreateDialog = () => {
    setNewName("");
    setError(null);
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    if (!catalogueKey) return;
    const name = (newName.trim() || suggestedName).trim();
    setCreating(true);
    setError(null);
    try {
      const created = await agentReviewAPI.createReviewSession(catalogueKey, name);
      setCreateOpen(false);
      setNewName("");
      onSessionIdChange(created.session_id);
      await onSessionsUpdated?.();
    } catch {
      setError("Couldn't create session.");
    } finally {
      setCreating(false);
    }
  };

  const handleDeleted = async () => {
    await onSessionsUpdated?.();
  };

  if (!catalogueKey) return null;

  return (
    <>
      <div
        className="flex w-full items-center gap-3 min-w-0"
        data-testid="agent-review-sessions"
      >
        {sessionsLoading ? (
          <FontAwesomeIcon
            icon={faSpinner}
            className="h-4 w-4 flex-shrink-0 animate-spin text-indigo-400"
            data-testid="agent-review-sessions-loading"
          />
        ) : null}
        {sessionOptions.length > 0 ? (
          <div className="min-w-0 flex-1 max-w-md">
            <SelectDropdown<string>
              inline
              label="Review session"
              buttonClassName={INTEGRATIONS_SELECT_BUTTON_CLASS}
              options={sessionOptions}
              value={selectedSessionId}
              onChange={onSessionIdChange}
              disabled={sessionsLoading}
            />
          </div>
        ) : (
          <p className="text-xs text-slate-500">No review session yet</p>
        )}
        <Button
          plain
          onClick={openCreateDialog}
          disabled={sessionsLoading}
          className="!text-slate-300 hover:!text-white flex-shrink-0"
        >
          <FontAwesomeIcon icon={faPlus} />
          New session
        </Button>
        {canDelete && selectedSessionId ? (
          <Button
            plain
            onClick={() => setDeleteOpen(true)}
            disabled={sessionsLoading}
            className="!text-slate-300 hover:!text-rose-300 flex-shrink-0"
            data-testid="agent-review-delete-session"
          >
            <FontAwesomeIcon icon={faTrashCan} />
            Delete session
          </Button>
        ) : null}
      </div>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} size="sm">
        <DialogTitle>New review session</DialogTitle>
        <DialogBody className="space-y-3">
          <p className="text-sm text-slate-400">
            Name this batch of reviews for {taskTypeLabel} — for example, a calibration round or
            meeting date.
          </p>
          <label className="block space-y-1.5">
            <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">
              Session name
            </span>
            <input
              type="text"
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
              placeholder={suggestedName}
              autoFocus
              className="w-full h-10 rounded-lg bg-white/5 px-3 text-sm text-slate-200 ring-1 ring-white/10 placeholder:text-slate-500 focus:outline-none focus:ring-indigo-400/40"
              data-testid="agent-review-new-session-name"
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  void handleCreate();
                }
              }}
            />
          </label>
          {error ? <p className="text-sm text-rose-400">{error}</p> : null}
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setCreateOpen(false)} disabled={creating}>
            Cancel
          </Button>
          <Button onClick={() => void handleCreate()} disabled={creating}>
            {creating ? "Creating…" : "Create session"}
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteSessionDialog
        open={deleteOpen}
        sessionId={selectedSessionId || null}
        sessionName={selectedSessionName}
        taskTypeLabel={taskTypeLabel}
        onClose={() => setDeleteOpen(false)}
        onDeleted={(result) => void handleDeleted().then(() => {
          if (sessionId === result.session_id) {
            const remaining = sessions.filter((session) => session.session_id !== result.session_id);
            onSessionIdChange(remaining[remaining.length - 1]?.session_id ?? null);
          }
        })}
      />
    </>
  );
}
